import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './context/AuthContext';
import { A11yProvider } from './context/A11yContext';
import { LangProvider, useLang } from './context/LangContext';
import TopNav from './components/TopNav';
import ProtectedRoute from './components/ProtectedRoute';
import Landing from './pages/Landing';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Questionnaire from './pages/Questionnaire';
import Results from './pages/Results';
import Applications from './pages/Applications';
import Documents from './pages/Documents';
import Assistant from './pages/Assistant';
import Directory from './pages/Directory';
import MissingBenefits from './pages/MissingBenefits';
import PartnerMode from './pages/PartnerMode';
import Handoff from './pages/Handoff';
import Equipment from './pages/Equipment';
import PartnerDashboard from './pages/PartnerDashboard';
import Appeals from './pages/Appeals';
import Helping from './pages/Helping';
import BundleDownload from './pages/BundleDownload';
import AdminStories from './pages/AdminStories';
import PaymentResult from './pages/PaymentResult';
import { InstallPrompt } from './components/InstallPrompt';

function AppRouter() {
  const location = useLocation();
  const { t } = useLang();
  // Handle OAuth session_id in URL hash before any routing
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <>
      <TopNav />
      <main>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/directory" element={<Directory />} />
          <Route path="/find-benefits" element={<ProtectedRoute><Questionnaire /></ProtectedRoute>} />
          <Route path="/results" element={<ProtectedRoute><Results /></ProtectedRoute>} />
          <Route path="/applications" element={<ProtectedRoute><Applications /></ProtectedRoute>} />
          <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
          <Route path="/assistant" element={<ProtectedRoute><Assistant /></ProtectedRoute>} />
          <Route path="/missing-benefits" element={<ProtectedRoute><MissingBenefits /></ProtectedRoute>} />
          <Route path="/partner" element={<ProtectedRoute><PartnerMode /></ProtectedRoute>} />
          <Route path="/handoff/:shareId" element={<Handoff />} />
          <Route path="/equipment" element={<ProtectedRoute><Equipment /></ProtectedRoute>} />
          <Route path="/partner/dashboard" element={<ProtectedRoute><PartnerDashboard /></ProtectedRoute>} />
          <Route path="/partner/team" element={<ProtectedRoute><PartnerDashboard team /></ProtectedRoute>} />
          <Route path="/appeals" element={<ProtectedRoute><Appeals /></ProtectedRoute>} />
          <Route path="/helping/:ownerId" element={<ProtectedRoute><Helping /></ProtectedRoute>} />
          <Route path="/bundle/:token" element={<BundleDownload />} />
          <Route path="/admin/stories" element={<ProtectedRoute><AdminStories /></ProtectedRoute>} />
          <Route path="/payment/success" element={<ProtectedRoute><PaymentResult /></ProtectedRoute>} />
          <Route path="/payment/cancel" element={<ProtectedRoute><PaymentResult cancelled /></ProtectedRoute>} />
        </Routes>
      </main>
      <InstallPrompt />
      <footer className="mt-16 border-t border-border py-8 text-center text-sm text-muted-foreground space-y-1">
        <div data-testid="footer-free">{t('100% free, forever. No premium tier, no hidden fees — every feature you see is free for everyone.')}</div>
        <div>{t('AccessBenefits Ontario · An independent navigator. Not a government service.')}</div>
      </footer>
      <Toaster position="top-right" richColors />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <A11yProvider>
          <LangProvider>
            <AppRouter />
          </LangProvider>
        </A11yProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
